import React, { FC } from 'react';

import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import CalendarTodayRounded from '@mui/icons-material/CalendarTodayRounded';

// TODO: Make this suck less
import { GameDay } from '~/core/types';
import { ScheduleGameDay } from '~/views/pages/dotcom/schedule/types';

import {
    BottomRow,
    DayOfMonth,
    DayOfWeekAbbrev,
    ExtraLargeNumberOfGames,
    LargeDayCard,
    LargeDayOfWeekAbbrev,
    LargeDayOfWeekLetter,
    LargeNumberOfGames,
    MonthDay,
    NumberOfGames,
    SmallDayCard,
    TopRow,
} from './styles';

type Props = {
    day: GameDay | ScheduleGameDay;
    isToday: boolean;
    isSelected: boolean;
    urlRoot: string;
    onDateUpdated?: (day: GameDay | ScheduleGameDay) => void;
};

const SPACEBAR_KEY_CODE = 'Space';

const getDayOfWeekLetter = (date: DateTime): string => date.toLocaleString({ weekday: 'narrow' });
const getDayOfWeekAbbrev = (date: DateTime): string => date.toLocaleString({ weekday: 'short' });
const getDayOfWeekFull = (date: DateTime): string => date.toLocaleString({ weekday: 'long' });

const getDayOfMonth = (date: DateTime): string => date.toLocaleString({ day: 'numeric' });
const getMonthAndDaySeparated = (date: DateTime): string => date.toLocaleString({ month: 'numeric', day: 'numeric' });
const getMonthAndDayReadout = (date: DateTime): string => date.toLocaleString({ month: 'long', day: 'numeric' });

const DayCard: FC<Props> = ({ day, isToday, isSelected, urlRoot, onDateUpdated }) => {
    const date = DateTime.fromISO(day.date);
    const navigate = useNavigate();
    const intl = useIntl();

    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (onDateUpdated) {
            e.preventDefault();
            onDateUpdated(day);
        }
    };

    // make link responsive to spacebar inputs like button elements are
    const handleKeyPress = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.code === SPACEBAR_KEY_CODE) {
            e.preventDefault();

            if (onDateUpdated) {
                onDateUpdated(day);
            } else {
                navigate(`${urlRoot}/${day.date}`);
            }
        }
    };

    const numberOfGamesMessage = intl.formatMessage(
        {
            description: 'How many games there are for the day',
            defaultMessage: '{games, plural, one {# Game} other {# Games}}',
        },
        { games: day.numberOfGames || 0 }
    );

    return (
        <>
            <SmallDayCard to={`${urlRoot}/${day.date}`} $isSelected={isSelected} onClick={handleOnClick}>
                <DayOfWeekAbbrev aria-label={getDayOfWeekFull(date)} $isSelected={isSelected}>
                    {getDayOfWeekAbbrev(date)}
                </DayOfWeekAbbrev>
                <DayOfMonth aria-label={getMonthAndDayReadout(date)} $isSelected={isSelected}>
                    {getDayOfMonth(date)}
                </DayOfMonth>
                <NumberOfGames aria-label={numberOfGamesMessage}>{day.numberOfGames}</NumberOfGames>
            </SmallDayCard>
            <LargeDayCard
                role="button"
                to={`${urlRoot}/${day.date}`}
                $isSelected={isSelected}
                onClick={handleOnClick}
                onKeyPress={handleKeyPress}
            >
                <TopRow>
                    <MonthDay aria-label={getMonthAndDayReadout(date)}>{getMonthAndDaySeparated(date)}</MonthDay>
                    <LargeDayOfWeekLetter aria-label={getDayOfWeekFull(date)}>
                        {getDayOfWeekLetter(date)}
                    </LargeDayOfWeekLetter>
                    <LargeDayOfWeekAbbrev aria-label={getDayOfWeekFull(date)}>
                        {getDayOfWeekAbbrev(date)}
                    </LargeDayOfWeekAbbrev>
                </TopRow>
                <BottomRow $isSelected={isSelected}>
                    <LargeNumberOfGames aria-label={numberOfGamesMessage}>{day.numberOfGames}</LargeNumberOfGames>
                    <ExtraLargeNumberOfGames>{numberOfGamesMessage}</ExtraLargeNumberOfGames>
                    {isToday && <CalendarTodayRounded />}
                </BottomRow>
            </LargeDayCard>
        </>
    );
};

export default DayCard;
